<section
  class="event-view"
  eventqShortcutScope
  eventqShortcutDocument
  (eventqShortcut)="onEventDetailsShortcut($event)"
>
  <eventq-disarm-site-popover
    [open]="cameraDisarmPopoverOpen()"
    [siteName]="selectedCameraArmDisarm()?.name ?? ''"
    title="Disarm Camera"
    entityFieldLabel="Disarm Camera:"
    confirmLabel="Disarm Camera"
    untilNextScheduleWarning="When a camera is disarmed until the next schedule, it will automatically re-arm at the next scheduled monitoring period."
    [reasons]="cameraDisarmReasons.value()?.items ?? []"
    [reasonId]="siteDisarmReasonId()"
    [disarmFor]="siteDisarmFor()"
    [fixedDurationMinutes]="siteDisarmFixedDurationMinutesForUi()"
    [fixedDurationCustom]="siteDisarmFixedDurationCustom()"
    [fixedDurationCustomDraft]="siteDisarmFixedDurationCustomDraft()"
    [untilAt]="siteDisarmUntilAt()"
    [ianaTimeZone]="disarmDisplayTimeZone()"
    [submitDisabled]="cameraArmDisarmSubmitting()"
    [anchor]="cameraArmDisarmAnchor()"
    reasonMenuPlacement="bottom-start"
    (closed)="onCameraArmDisarmClose()"
    (reasonIdChange)="onCameraDisarmReasonChange($event)"
    (disarmForChange)="onCameraDisarmForChange($event)"
    (fixedDurationMinutesChange)="onCameraDisarmFixedDurationChange($event)"
    (fixedDurationCustomChange)="onCameraDisarmFixedDurationCustomChange($event)"
    (fixedDurationCustomDraftChange)="onCameraDisarmFixedDurationCustomDraftChange($event)"
    (untilAtChange)="onCameraDisarmUntilAtChange($event)"
    (confirm)="onCameraDisarmConfirm($event)"
  />

  <eventq-activate-talkdown-popover
    [open]="activateTalkdownOpen()"
    [title]="activateTalkdownTitle()"
    [options]="activateTalkdownOptions()"
    (closed)="onActivateTalkdownClose()"
    (activate)="onActivateTalkdownConfirm($event)"
  />

  @if (detail.isLoading() && !viewDetail()) {
    <eventq-action-loader fill="panel" message="Loading Event" />
  } @else if (detail.error() && !viewDetail()) {
    <p class="event-view__error" role="alert">Unable to load event details. Try again.</p>
  } @else if (viewDetail(); as event) {
    <eventq-event-meta-bar
      [title]="event.location"
      [eventId]="event.eventId"
      [address]="event.address"
      [currentTime]="siteClock.formattedTime()"
    >
      <div eventqEventMetaActions class="event-view__header-actions">
        <eventq-button
          label="Sync with Resolve"
          variant="primary-outline"
          size="sm"
          [iconSrc]="'/assets/events/icons/resolve-emblem.svg' | prefixUrl"
          [disabled]="siblingSync.syncing()"
          (pressed)="onShowDetailsInResolve()"
        />

        @if (access.canRaise()) {
          <div class="event-view__header-action">
            <eventq-button
              #raiseTrigger
              label="Raise to Resolve"
              variant="tertiary"
              size="sm"
              [iconSrc]="'/assets/events/icons/trending-up.svg' | prefixUrl"
              [active]="raiseActionActive()"
              (pressed)="onRaiseToResolve()"
            />

            @if (browserTabs.eventViewPopover() === 'raise-to-resolve') {
              <eventq-raise-event-popover
                [open]="true"
                [groups]="raiseOutcomes.value()?.groups ?? []"
                [searchQuery]="browserTabs.eventViewPopoverSearchQuery()"
                [selectedPriorities]="browserTabs.eventViewPopoverPriorities()"
                [selectedOutcomeId]="browserTabs.eventViewPopoverSelectedOutcomeId()"
                [anchor]="raiseTrigger.hostElement"
                panelWidth="533px"
                (closed)="onPopoverClose()"
                (searchQueryChange)="onPopoverSearchChange($event)"
                (selectedPrioritiesChange)="onPopoverPrioritiesChange($event)"
                (selectedOutcomeIdChange)="onPopoverOutcomeSelect($event)"
                (confirm)="onRaiseConfirm($event)"
              />
            }
          </div>
        }

        @if (showParkAction()) {
          <div class="event-view__header-action">
            <eventq-button
              #parkTrigger
              label="Park Event"
              variant="tertiary"
              size="sm"
              [iconSrc]="'/assets/events/icons/park-event.svg' | prefixUrl"
              [active]="parkActionActive()"
              (pressed)="onParkEvent()"
            />

            @if (browserTabs.eventViewPopover() === 'park-event') {
              <eventq-park-event-popover
                [open]="true"
                [selectedDurationMinutes]="selectedParkDuration()"
                [anchor]="parkTrigger.hostElement"
                (closed)="onPopoverClose()"
                (selectedDurationMinutesChange)="onParkDurationChange($event)"
                (confirm)="onParkConfirm($event)"
              />
            }
          </div>
        }

        @if (access.canClose()) {
          <div class="event-view__header-action">
            <eventq-button
              #closeTrigger
              label="Close Event"
              variant="danger"
              size="sm"
              [iconSrc]="'/assets/events/icons/meeting-room.svg' | prefixUrl"
              [active]="closeActionActive()"
              (pressed)="onCloseEvent()"
            />

            @if (browserTabs.eventViewPopover() === 'close-event') {
              <eventq-close-event-popover
                [open]="true"
                [groups]="closeOutcomes.value()?.groups ?? []"
                [searchQuery]="browserTabs.eventViewPopoverSearchQuery()"
                [selectedPriorities]="browserTabs.eventViewPopoverPriorities()"
                [selectedOutcomeId]="browserTabs.eventViewPopoverSelectedOutcomeId()"
                [closingDetails]="browserTabs.eventViewPopoverClosingDetails()"
                [submitAttempted]="popoverSubmitAttempted()"
                [anchor]="closeTrigger.hostElement"
                panelWidth="533px"
                (closed)="onPopoverClose()"
                (searchQueryChange)="onPopoverSearchChange($event)"
                (selectedPrioritiesChange)="onPopoverPrioritiesChange($event)"
                (selectedOutcomeIdChange)="onPopoverOutcomeSelect($event)"
                (closingDetailsChange)="onPopoverClosingDetailsChange($event)"
                (confirm)="onCloseConfirm($event)"
              />
            }
          </div>
        }
      </div>
    </eventq-event-meta-bar>

    <div
      class="event-view__main-tabs"
      [class.event-view__main-tabs--grid]="showCameraGridChrome()"
    >
      @if (showCameraGridChrome()) {
        <eventq-button
          class="event-view__show-camera-list"
          [class.event-view__show-camera-list--open]="cameraListOpen()"
          label="Show Camera List"
          variant="tertiary"
          size="sm"
          [iconSrc]="'/assets/events/icons/show-camera-list.svg' | prefixUrl"
          [badgeCount]="eventCameras().length"
          [active]="cameraListOpen()"
          (pressed)="onToggleCameraList()"
        />

        <div class="event-view__grid-controls">
          @if (browserTabs.eventViewTab() === 'playback') {
            <div
              class="event-view__cameras-datetime"
              [class.event-view__cameras-datetime--invalid]="!camerasPlaybackAnchorAt()"
            >
              <eventq-datetime-picker
                mode="field"
                fieldLabel="Select Date & Time for All Cameras"
                fieldPlaceholder="Select"
                [required]="true"
                panelPlacement="below"
                panelAlign="end"
                [value]="camerasPlaybackAnchorDate()"
                [max]="camerasPlaybackPickerMax()"
                [timeZoneAbbreviation]="camerasPlaybackTimeZoneAbbreviation()"
                (applied)="onCamerasPlaybackAnchorApplied($event)"
              />
            </div>
          }
          <div class="event-view__layout-pages">
            <eventq-select
              class="event-view__layout-select"
              [options]="cameraLayoutOptions"
              [value]="cameraGridLayout()"
              (valueChange)="onCameraGridLayoutChange($event)"
            />
            <eventq-camera-pages
              [page]="cameraGridPage()"
              [totalPages]="cameraGridTotalPages()"
              (pageChange)="onCameraGridPageChange($event)"
            />
          </div>
        </div>
      }

      <div class="event-view__main-tabs-end">
        <eventq-toggle-tab
          variant="pill"
          [items]="mainTabs"
          [activeId]="browserTabs.eventViewTab()"
          (activeIdChange)="onMainTabChange($event)"
        />
      </div>
    </div>

    @if (browserTabs.eventViewTab() === 'alarms') {
      <div class="event-view__center">
        <app-event-playback-host
          class="event-view__playback"
          mode="playback"
          streamType="sub"
          videoObjectFit="contain"
          fullscreenMode="popover"
          [eventId]="eventId()"
          [siteId]="eventDeviceSiteId()"
          [cameraId]="playbackCameraId()"
          [cameras]="eventCameras()"
          [headerTitle]="playbackHeaderTitle()"
          [headerTimestamp]="playbackHeaderTimestamp()"
          [visibleDetectionTypes]="playbackVisibleDetectionTypes()"
          [playbackViewMode]="anchoredPlaybackViewMode()"
          [playbackCustomRangeSeconds]="anchoredPlaybackCustomRangeSeconds()"
          [playbackAnchorTimestamp]="anchoredPlaybackTimestamp()"
          [playbackAnchorMode]="anchoredPlaybackAnchorMode()"
          [selectedAlarmId]="attachClipAlarmId()"
          [alarmClipUrl]="selectedAlarmClipUrl()"
          [alarmClipUrls]="selectedAlarmClipUrls()"
          [alarmDetections]="selectedAlarmDetections()"
          [alarmTimestamp]="selectedAlarmTimestamp()"
          [allowClipToggle]="true"
          [snapshotEnabled]="access.canAttachSnapshot()"
          [attachAlarmClipEnabled]="access.canAttachAlarmClip()"
          (playbackViewModeChange)="onPlaybackViewModeChange($event)"
          (playbackAnchorTimestampChange)="onPlaybackAnchorTimestampChange($event)"
          (fullscreenRequest)="onAlarmFullscreenRequest($event)"
          (alarmClipViewed)="onAlarmClipViewed($event)"
        />

        <div
          class="event-view__side-panels"
          [class.event-view__side-panels--cameras]="browserTabs.sidePanel() === 'cameras'"
          [class.event-view__side-panels--talkdowns]="browserTabs.sidePanel() === 'talkdowns'"
        >
          <eventq-collapsible-search-panel
            title="Cameras"
            [open]="browserTabs.sidePanel() === 'cameras'"
            [fillHeight]="browserTabs.sidePanel() === 'cameras'"
            [items]="filteredCameras()"
            [selectedId]="selectedCameraId()"
            [searchQuery]="browserTabs.activePageState().cameraSearchQuery"
            (openChange)="onCamerasPanelToggle($event)"
            (searchQueryChange)="onCameraSearch($event)"
            (itemSelect)="onCameraSelect($event)"
          />

          <div class="event-view__side-panels-bottom">
            <eventq-collapsible-search-panel
              title="Talkdowns"
              [open]="browserTabs.sidePanel() === 'talkdowns'"
              [fillHeight]="browserTabs.sidePanel() === 'talkdowns'"
              [padListBottom]="browserTabs.sidePanel() === 'talkdowns'"
              [items]="filteredTalkdowns().map((td) => ({ id: td.id, label: td.speakerName }))"
              [searchQuery]="browserTabs.activePageState().talkdownSearchQuery"
              (openChange)="onTalkdownsPanelToggle($event)"
              (searchQueryChange)="onTalkdownSearch($event)"
            >
              <div eventqCollapsibleSearchPanelBody>
                @for (group of filteredTalkdowns(); track group.id) {
                  <eventq-talkdown-group
                    [speakerName]="group.speakerName"
                    [cameraLabels]="group.cameraLabels"
                    (activate)="onActivateTalkdownOpen(group.id)"
                  />
                }
              </div>
            </eventq-collapsible-search-panel>
          </div>
        </div>

        <!-- Live always streams now; the cameras date only drives the Playback tab. -->
        <app-event-playback-host
          class="event-view__live"
          mode="live"
          videoObjectFit="contain"
          fullscreenMode="popover"
          [snapshotEnabled]="access.canAttachSnapshot()"
          [attachAlarmClipEnabled]="false"
          [eventId]="eventId()"
          [siteId]="eventDeviceSiteId()"
          [siteName]="eventReportSiteName()"
          [cameraId]="liveCameraId()"
          [cameras]="eventCameras()"
          [headerTitle]="liveCamera()?.name ?? 'Live Camera'"
          [streamType]="browserTabs.activePageState().liveStreamType"
          [showPtzToggle]="liveCamera()?.isPtz === true"
          (streamTypeChange)="onLiveStreamTypeChange($event)"
          (fullscreenRequest)="onLiveCameraPopoutToggle(liveCameraId())"
        />
      </div>
    } @else if (browserTabs.eventViewTab() === 'live-cameras') {
      <div class="event-view__camera-workspace">
        @if (cameraListOpen()) {
          <eventq-camera-list-panel
            class="event-view__camera-list"
            [items]="cameraGridListItems()"
            [openIds]="selectedCameraIds()"
            [searchQuery]="cameraGridSearchQuery()"
            (searchQueryChange)="onCameraGridSearch($event)"
            (toggleCamera)="onToggleGridCamera($event)"
            (openAll)="onOpenAllCameras()"
          />
        }

        <div class="event-view__camera-grid-wrap">
          <ng-template #liveGridCell let-cameraId>
            @if (cameraById(cameraId); as camera) {
              <div class="event-view__grid-tile">
                <!-- Live always streams now; the cameras date only drives the Playback tab. -->
                <app-event-playback-host
                  mode="live"
                  videoObjectFit="contain"
                  fullscreenMode="popover"
                  [snapshotEnabled]="access.canAttachSnapshot()"
                  [attachAlarmClipEnabled]="false"
                  [eventId]="eventId()"
                  [siteId]="eventDeviceSiteId()"
                  [siteName]="eventReportSiteName()"
                  [cameraId]="camera.id"
                  [cameras]="eventCameras()"
                  [headerTitle]="camera.name"
                  [streamType]="browserTabs.activePageState().liveStreamType"
                  [showPtzToggle]="camera.isPtz"
                  (streamTypeChange)="onLiveStreamTypeChange($event)"
                  (fullscreenRequest)="onLiveCameraPopoutToggle(camera.id)"
                />
              </div>
            }
          </ng-template>

          <eventq-camera-grid
            [layout]="gridLayout()"
            [cameraIds]="pageCameraIds()"
            [cellTemplate]="liveGridCell"
          />
        </div>
      </div>
    } @else {
      <div class="event-view__camera-workspace">
        @if (cameraListOpen()) {
          <eventq-camera-list-panel
            class="event-view__camera-list"
            [items]="cameraGridListItems()"
            [openIds]="selectedCameraIds()"
            [searchQuery]="cameraGridSearchQuery()"
            (searchQueryChange)="onCameraGridSearch($event)"
            (toggleCamera)="onToggleGridCamera($event)"
            (openAll)="onOpenAllCameras()"
          />
        }

        <div class="event-view__camera-grid-wrap">
          <ng-template #playbackGridCell let-cameraId>
            @if (cameraById(cameraId); as camera) {
              <div class="event-view__grid-tile">
                <app-event-playback-host
                  mode="playback"
                  streamType="sub"
                  videoObjectFit="contain"
                  fullscreenMode="library"
                  [allowClipToggle]="false"
                  [snapshotEnabled]="access.canAttachSnapshot()"
                  [attachAlarmClipEnabled]="false"
                  [eventId]="eventId()"
                  [siteId]="eventDeviceSiteId()"
                  [cameraId]="camera.id"
                  [cameras]="eventCameras()"
                  [headerTitle]="camera.name"
                  [playbackViewMode]="anchoredPlaybackViewMode()"
                  [playbackCustomRangeSeconds]="anchoredPlaybackCustomRangeSeconds()"
                  [playbackAnchorTimestamp]="anchoredPlaybackTimestamp()"
                  [playbackAnchorMode]="anchoredPlaybackAnchorMode()"
                />
              </div>
            }
          </ng-template>

          <eventq-camera-grid
            [layout]="gridLayout()"
            [cameraIds]="pageCameraIds()"
            [cellTemplate]="playbackGridCell"
          />
        </div>
      </div>
    }

    @if (browserTabs.eventViewTab() === 'alarms') {
      <section class="event-view__alarms-footer">
        <div class="event-view__alarms-toolbar">
          <div class="event-view__alarms-toolbar-left">
            @if (browserTabs.activePageState().alarmsFooterTab === 'alarms') {
              <eventq-button
                class="event-view__flag-all"
                label="Flag All Alarms"
                variant="tertiary"
                size="sm"
                [badgeCount]="unflaggedCount()"
                [disabled]="flagAllDisabled()"
                (pressed)="onFlagAllAlarms()"
              />
              <div class="event-view__filters">
                <eventq-accent-checkbox
                  label="Motion"
                  variant="motion"
                  [checked]="browserTabs.alarmFilters().motion"
                  (checkedChange)="onFilterChange('motion', $event)"
                />
                <eventq-accent-checkbox
                  label="Human"
                  variant="human"
                  [checked]="browserTabs.alarmFilters().human"
                  (checkedChange)="onFilterChange('human', $event)"
                />
                <eventq-accent-checkbox
                  label="Vehicle"
                  variant="vehicle"
                  [checked]="browserTabs.alarmFilters().vehicle"
                  (checkedChange)="onFilterChange('vehicle', $event)"
                />
                <eventq-accent-checkbox
                  label="Gun"
                  variant="gun"
                  [checked]="browserTabs.alarmFilters().gun"
                  (checkedChange)="onFilterChange('gun', $event)"
                />
              </div>
            } @else {
              <eventq-popover-search
                class="event-view__arm-disarm-search"
                placeholder="Search"
                [value]="armDisarmSearchQuery()"
                (valueChange)="onArmDisarmSearch($event)"
              />
            }
          </div>

          <div class="event-view__alarms-toolbar-right">
            <eventq-lined-tab
              label="Alarms"
              [showCount]="false"
              [active]="browserTabs.activePageState().alarmsFooterTab === 'alarms'"
              (selected)="onAlarmsFooterTabChange('alarms')"
            />
            <eventq-lined-tab
              label="Disarm"
              [showCount]="false"
              [active]="browserTabs.activePageState().alarmsFooterTab === 'arm-disarm'"
              (selected)="onAlarmsFooterTabChange('arm-disarm')"
            />
          </div>
        </div>

        @if (browserTabs.activePageState().alarmsFooterTab === 'alarms') {
          <eventq-data-table
            #alarmsTable
            class="event-view__alarms-table"
            density="compact"
            [rowSpaceActivates]="false"
            [columns]="alarmColumns"
            [rows]="alarmRows()"
            [sort]="alarmSort()"
            [selectedRowId]="alarmsTableSelectedRowId()"
            [loading]="alarms.isLoading() && alarmItems().length === 0"
            [skeletonCount]="5"
            [fillRemainingHeight]="true"
            [showLabelWhenEmpty]="true"
            emptyLabel="No Alarms in this site."
            [emptyIconSrc]="'/assets/events/icons/no-alarms.svg' | prefixUrl"
            (sortChange)="onAlarmSort($event)"
            (scrollChange)="onAlarmsTableScroll($event)"
            (flagChange)="onFlagChange($event)"
            (rowClick)="onAlarmRowClick($event)"
          />
        } @else {
          <eventq-data-table
            class="event-view__alarms-table event-view__alarms-table--arm-disarm"
            density="compact"
            [columns]="armDisarmColumns()"
            [rows]="armDisarmRows()"
            [sort]="armDisarmSort()"
            [armStatusInteractive]="canArmDisarmCamera()"
            [loading]="cameras.isLoading() && eventCameras().length === 0"
            [skeletonCount]="5"
            [fillRemainingHeight]="true"
            [showLabelWhenEmpty]="true"
            [emptyLabel]="armDisarmEmptyLabel()"
            [emptyIconSrc]="'/assets/events/icons/security-camera.svg' | prefixUrl"
            (sortChange)="onArmDisarmSort($event)"
            (armStatusClick)="onArmStatusClick($event)"
            (rowClick)="onArmDisarmRowClick($event)"
          />
        }
      </section>
    }

    @if (liveCameraPopoutCamera(); as popoutCamera) {
      <eventq-dialog-popover
        class="event-view__live-popout"
        [open]="true"
        [title]="popoutCamera.name"
        [hideHeader]="true"
        [showFooter]="false"
        [bodyScroll]="false"
        placement="center"
        panelWidth="75vw"
        panelHeight="75vh"
        panelMaxHeight="75vh"
        (closed)="onLiveCameraPopoutClose()"
      >
        <app-event-playback-host
          mode="live"
          videoObjectFit="contain"
          fullscreenMode="popover"
          [fullscreenActive]="true"
          [snapshotEnabled]="access.canAttachSnapshot()"
          [attachAlarmClipEnabled]="false"
          [eventId]="eventId()"
          [siteId]="eventDeviceSiteId()"
          [siteName]="eventReportSiteName()"
          [cameraId]="popoutCamera.id"
          [cameras]="eventCameras()"
          [headerTitle]="popoutCamera.name"
          [streamType]="browserTabs.activePageState().liveStreamType"
          [showPtzToggle]="popoutCamera.isPtz"
          (streamTypeChange)="onLiveStreamTypeChange($event)"
          (fullscreenRequest)="onLiveCameraPopoutClose()"
        />
      </eventq-dialog-popover>
    }

    @if (viewAlarmOpen()) {
      <eventq-view-alarm-popover
        [open]="true"
        [cameraName]="viewAlarmCamera()?.name ?? 'Alarm'"
        [urlValue]="viewAlarmUrl()"
        [flagged]="viewAlarmFlagged()"
        [hasPrevAlarm]="viewAlarmHasPrev()"
        [hasNextAlarm]="viewAlarmHasNext()"
        (closed)="onViewAlarmClose()"
        (prevAlarm)="onViewAlarmPrev()"
        (nextAlarm)="onViewAlarmNext()"
        (flaggedChange)="onViewAlarmFlagChange($event)"
      >
        <app-event-playback-host
          mode="playback"
          streamType="main"
          videoSource="webrtc"
          aiFeedbackScope="view-alarm"
          [eventId]="eventId()"
          [cameraId]="viewAlarmCameraId()"
          [cameras]="eventCameras()"
          [headerTitle]="viewAlarmHeaderTitle()"
          [headerTimestamp]="viewAlarmHeaderTimestamp()"
          [visibleDetectionTypes]="viewAlarmVisibleDetectionTypes()"
          [playbackViewMode]="browserTabs.activePageState().viewAlarmPlaybackViewMode"
          [playbackCustomRangeSeconds]="anchoredPlaybackCustomRangeSeconds()"
          [playbackAnchorTimestamp]="browserTabs.activePageState().viewAlarmPlaybackAnchorTimestamp"
          [selectedAlarmId]="viewAlarmSelectedAlarmId()"
          [alarmClipUrl]="viewAlarmClipUrl()"
          [alarmClipUrls]="viewAlarmClipUrls()"
          [alarmDetections]="viewAlarmAlarmContext()?.detections ?? []"
          [alarmTimestamp]="viewAlarmAlarmContext()?.timestamp ?? null"
          playbackAnchorMode="start"
          [showFullscreenAction]="false"
          [snapshotEnabled]="access.canAttachSnapshot()"
          [attachAlarmClipEnabled]="access.canAttachAlarmClip()"
          videoObjectFit="contain"
          (playbackViewModeChange)="onViewAlarmPlaybackViewModeChange($event)"
          (playbackAnchorTimestampChange)="onViewAlarmPlaybackAnchorTimestampChange($event)"
          (alarmClipViewed)="onAlarmClipViewed($event)"
        />
      </eventq-view-alarm-popover>
    }
  }
</section>
